// search_widget.js — header-driven search (desktop panel + mobile sheet)
import React, { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import './css/search.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faPlus,
  faPlay,
  faChevronDown,
  faChevronLeft,
  faEllipsisVertical,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const SearchWidget = forwardRef(({
  tracks,
  onTrackClick,
  onAddToQueue,
  appendMode = false,
  onPlaySearchResults,
  onDeleteSearchResults,
  requestAddTrack,
}, ref) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewStack, setViewStack] = useState([{ type: 'search', title: 'Search' }]);
  const popView = () => setViewStack(s => (s.length > 1 ? s.slice(0, -1) : s));
  const currentView = viewStack[viewStack.length - 1];

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  /** Local library filter vs server discovery results (iOS filteredTracks vs SearchResultsSheet). */
  const [resultsAreLocal, setResultsAreLocal] = useState(true);

  const inputRef = useRef(null);
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const dragging = useRef(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const resetToRoot = () => setViewStack([{ type: 'search', title: 'Search' }]);

  const closeSearch = () => {
    setSheetOpen(false);
    setSearchOpen(false);
    setSearchTerm('');
    setResultsAreLocal(true);
    resetToRoot();
    setTimeout(() => setSearchResults([]), 200);
  };

  const openSearch = () => {
    if (isMobile) setSheetOpen(true);
    else setSearchOpen(true);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  useImperativeHandle(ref, () => ({
    open: openSearch,
    close: closeSearch,
  }));

  useEffect(() => {
    const handleTouchStart = (e) => {
      dragging.current = true;
      startY.current = e.touches[0].clientY;
    };
    const handleTouchMove = (e) => {
      if (!dragging.current) return;
      currentY.current = e.touches[0].clientY;
      const deltaY = currentY.current - startY.current;
      if (deltaY > 0 && sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    };
    const handleTouchEnd = () => {
      dragging.current = false;
      const deltaY = currentY.current - startY.current;
      if (deltaY > 100) {
        closeSearch();
      } else if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)';
      }
    };

    const sheet = sheetRef.current;
    if (sheet) {
      sheet.addEventListener('touchstart', handleTouchStart);
      sheet.addEventListener('touchmove', handleTouchMove);
      sheet.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      if (sheet) {
        sheet.removeEventListener('touchstart', handleTouchStart);
        sheet.removeEventListener('touchmove', handleTouchMove);
        sheet.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [sheetOpen]);

  const filterLocalTracks = (keyword) => {
    const lower = keyword.toLowerCase();
    const parts = lower.split(' ').filter(Boolean);
    return (tracks || []).filter(track =>
      parts.every(sub =>
        (track.title || '').toLowerCase().includes(sub) ||
        (track.artist || '').toLowerCase().includes(sub)
      )
    );
  };

  const handleSearchChange = (e) => {
    const keyword = e.target.value;
    setSearchTerm(keyword);

    if (!keyword.trim()) {
      setSearchResults([]);
      setResultsAreLocal(true);
      return;
    }

    setResultsAreLocal(true);
    setSearchResults(filterLocalTracks(keyword));
  };

  const runSearchQuery = (keyword) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');

    if (!keyword) return;
    setIsLoading(true);

    fetch(`/api/music/search?user_id=${user_id}&title=${encodeURIComponent(keyword)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const tagged = Array.isArray(data) ? data.map(t => ({ ...t, source: 'server' })) : [];
        setResultsAreLocal(false);
        setSearchResults(tagged);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      const kw = searchTerm.trim();
      if (!kw) return;
      runSearchQuery(kw);
    }
  };

  const handleSearchClick = () => {
    const kw = searchTerm.trim();
    if (!searchOpen && !sheetOpen) {
      openSearch();
      return;
    }
    if (kw) runSearchQuery(kw);
    else closeSearch();
  };

  /** List-level Play — creates a temporary search playlist from all local matches. */
  const playAllLocalResults = () => {
    const kw = searchTerm.trim();
    if (!kw || !resultsAreLocal || !searchResults.length || !onPlaySearchResults) return;
    onPlaySearchResults(searchResults, kw, { shuffle: false });
    closeSearch();
  };

  const deleteAllLocalResults = async () => {
    const kw = searchTerm.trim();
    if (!kw || !resultsAreLocal || !searchResults.length || !onDeleteSearchResults || isDeleting) return;
    const n = searchResults.length;
    const ok = window.confirm(
      n === 1
        ? `Delete the 1 track matching “${kw}”? This cannot be undone.`
        : `Delete all ${n} tracks matching “${kw}”? This cannot be undone.`
    );
    if (!ok) return;
    setIsDeleting(true);
    try {
      await onDeleteSearchResults(searchResults);
      setSearchResults([]);
      setSearchTerm('');
      closeSearch();
    } finally {
      setIsDeleting(false);
    }
  };

  /** Single result: play only that track — do not create a search playlist. */
  const handlePlayTrack = (track) => {
    const owned = tracks.find(t => t.video_id === track.video_id);
    if (owned) {
      const index = tracks.findIndex(t => t.video_id === track.video_id);
      onTrackClick(index, owned);
    } else {
      requestAddTrack(track);
    }
    closeSearch();
  };

  const handleAddTrack = (track, e) => {
    e?.stopPropagation?.();
    const owned = tracks.find(t => t.video_id === track.video_id);
    if (owned && appendMode && onAddToQueue) {
      onAddToQueue(owned);
      return;
    }
    handlePlayTrack(track);
  };

  const renderActionOverlay = (track) => {
    const owned = tracks.find(t => t.video_id === track.video_id);
    const showAdd = !owned || appendMode;
    const icon = showAdd ? faPlus : faPlay;

    const onOverlayActivate = (e) => {
      e.stopPropagation();
      handleAddTrack(track, e);
    };

    return (
      <div
        className={`overlay${isMobile ? ' show' : ''}`}
        onClick={onOverlayActivate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOverlayActivate(e);
          }
        }}
        aria-label={showAdd ? (owned ? 'Add track to playing list' : 'Add track') : 'Play track'}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
    );
  };

  const openTrackSheet = (track) => {
    window.dispatchEvent(new CustomEvent('ui:openTrackSheet', { detail: { track } }));
  };

  const haveIds = useMemo(
    () => new Set((tracks || []).map(t => t.video_id || t.id)),
    [tracks]
  );

  const trimmedTerm = searchTerm.trim();
  const canActOnLocalResults = Boolean(
    resultsAreLocal && !isLoading && searchResults.length > 0 && trimmedTerm
  );

  const searchFieldActions = (
    <>
      {canActOnLocalResults && onPlaySearchResults && (
        <button
          type="button"
          className="search-list-action primary"
          onClick={playAllLocalResults}
          aria-label={appendMode ? `Add results for ${trimmedTerm} to playing list` : `Play all results for ${trimmedTerm}`}
          title={appendMode ? `Add “${trimmedTerm}”` : `Play “${trimmedTerm}”`}
        >
          <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
          <span>{appendMode ? 'Add' : 'Play'}</span>
        </button>
      )}
      {canActOnLocalResults && onDeleteSearchResults && (
        <button
          type="button"
          className="search-list-action danger"
          onClick={deleteAllLocalResults}
          disabled={isDeleting}
          aria-label={`Delete all results for ${trimmedTerm}`}
          title={`Delete “${trimmedTerm}”`}
        >
          <FontAwesomeIcon icon={faTrash} />
          <span>{isDeleting ? 'Deleting…' : 'Delete'}</span>
        </button>
      )}
      <button
        type="button"
        className="search-submit"
        onClick={isMobile
          ? () => trimmedTerm && runSearchQuery(trimmedTerm)
          : handleSearchClick}
        aria-label="Submit search"
      >
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
      </button>
    </>
  );

  const resultsList = (
    <>
      {isLoading && <div className="search-empty">Searching…</div>}
      {!isLoading && (searchResults.length > 0 ? (
        searchResults.map((track, i) => (
          <div key={i} className="search-item">
            <div className="search-thumb" onClick={(e) => handleAddTrack(track, e)}>
              <img src={track.thumbnail_default || track.thumbnail_medium} alt={track.title} />
              {renderActionOverlay(track)}
            </div>
            <div
              className="search-meta"
              onClick={() => handlePlayTrack(track)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePlayTrack(track);
                }
              }}
            >
              <div className="title">{track.title}</div>
              <div className="artist">{track.artist}</div>
            </div>
            {haveIds.has(track.video_id || track.id) && (
              <div className="search-actions">
                <button
                  type="button"
                  className="sr-more-btn"
                  aria-label="More actions"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTrackSheet(track);
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </button>
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="search-empty">
          Enter a track or artist, then press Enter.
        </div>
      ))}
    </>
  );

  return (
    <>
      {!isMobile && searchOpen && (
        <>
          <div className="search-backdrop" onClick={closeSearch} aria-hidden="true" />
          <div className="search-desktop" role="dialog" aria-modal="true" aria-label="Search">
            <div className="search-desktop-header">
              <span>Search</span>
              <button type="button" className="search-close" onClick={closeSearch} aria-label="Close search">
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>

            <div className="search-desktop-input">
              <div className="search-shell">
                <input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchSubmit}
                  placeholder="Search title or artist…"
                  className="search-input"
                  aria-label="Search input"
                />
                {searchFieldActions}
              </div>
            </div>

            <div className="search-results">
              {resultsList}
            </div>
          </div>
        </>
      )}

      {isMobile && sheetOpen && (
        <div
          ref={sheetRef}
          className="search-sheet full"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
        >
          <div className="search-header">
            <div className="left">
              {viewStack.length > 1 ? (
                <button type="button" className="search-back" onClick={popView} aria-label="Back">
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              ) : (
                <button type="button" className="search-close" onClick={closeSearch} aria-label="Close search">
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
              )}
            </div>

            <div className="search-shell">
              <input
                ref={inputRef}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchSubmit}
                placeholder="Search title or artist…"
                className="search-input"
                aria-label="Search"
              />
              {searchFieldActions}
            </div>
          </div>

          <div className="search-content">
            {currentView.type === 'search' && (
              <div className="search-results">
                {resultsList}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});

export default SearchWidget;
